import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";
import {
  createWhitelistedIp,
  deleteWhitelistedIpById,
  getClientPbxSettings,
  parseClientEmail,
  getWhitelistedIpsByClient,
  updateClientPbxSettings,
} from "../services/pbxConfigService";
import {
  extractParserData,
  formatParsedPreview,
  hasParserValues,
  normalizeMultilineText,
  normalizeWhitelistedRecords,
  parseEmailBodyLocally,
  resolveParserDelimiter,
  sanitizeIpList,
} from "../services/pbxConfigMappers";

export const loadPbxConfigThunk = createAsyncThunk(
  "pbxConfig/load",
  async (clientId, { rejectWithValue }) => {
    const clientResponse = await getClientPbxSettings(clientId);

    if (clientResponse?.error) {
      return rejectWithValue(
        clientResponse.error.message || "Unable to load PBX configuration.",
      );
    }

    const whitelistResponse = await getWhitelistedIpsByClient(clientId, {
      limit: 100,
      offset: 0,
    });

    const records = whitelistResponse?.error
      ? []
      : normalizeWhitelistedRecords(
          whitelistResponse?.whitelistedIps || whitelistResponse?.whitelisted_ips,
        );

    return {
      clientId: Number(clientId),
      callerIdTag: String(clientResponse?.client?.pbx_tag_format || ""),
      enforceTLS: Boolean(clientResponse?.client?.tls_encryption_enabled),
      ipWhitelistRecords: records,
      persistedIpRecords: records,
      warning: whitelistResponse?.error?.message || "",
    };
  },
);

export const savePbxConfigThunk = createAsyncThunk(
  "pbxConfig/save",
  async (clientId, { getState, rejectWithValue }) => {
    const state = getState().pbxConfig;

    const clientResponse = await getClientPbxSettings(clientId);
    if (clientResponse?.error || !clientResponse?.client?.raw) {
      return rejectWithValue(
        clientResponse?.error?.message || "Unable to load client PBX settings.",
      );
    }

    const saveClientResponse = await updateClientPbxSettings(
      clientId,
      clientResponse.client.raw,
      {
        pbxTagFormat: state.callerIdTag,
        tlsEncryptionEnabled: state.enforceTLS,
      },
    );

    if (saveClientResponse?.error) {
      return rejectWithValue(
        saveClientResponse.error.message || "Unable to save PBX configuration.",
      );
    }

    const currentRecords = normalizeWhitelistedRecords(state.ipWhitelistRecords);
    const persistedRecords = normalizeWhitelistedRecords(state.persistedIpRecords);

    const currentIps = sanitizeIpList(currentRecords.map((item) => item.ip_address));
    const currentIpSet = new Set(currentIps);
    const persistedByIp = new Map(
      persistedRecords.map((item) => [item.ip_address, item]),
    );

    const ipAddressesToCreate = currentIps.filter((ip) => !persistedByIp.has(ip));
    const recordsToDelete = persistedRecords.filter(
      (item) => item.id && !currentIpSet.has(item.ip_address),
    );

    const createResponses = await Promise.all(
      ipAddressesToCreate.map((ip_address) =>
        createWhitelistedIp({ client_id: clientId, ip_address }),
      ),
    );

    if (createResponses.some((response) => response?.error)) {
      const firstFailure = createResponses.find((response) => response?.error);
      return rejectWithValue(
        firstFailure?.error?.message || "Unable to create whitelisted IP.",
      );
    }

    const deleteResponses = await Promise.all(
      recordsToDelete.map((item) => deleteWhitelistedIpById(item.id)),
    );

    if (deleteResponses.some((response) => response?.error)) {
      const firstFailure = deleteResponses.find((response) => response?.error);
      return rejectWithValue(
        firstFailure?.error?.message || "Unable to delete whitelisted IP.",
      );
    }

    const refreshedWhitelist = await getWhitelistedIpsByClient(clientId, {
      limit: 100,
      offset: 0,
    });

    if (refreshedWhitelist?.error) {
      return rejectWithValue(
        refreshedWhitelist.error.message || "Unable to refresh whitelisted IPs.",
      );
    }

    const refreshedRecords = normalizeWhitelistedRecords(
      refreshedWhitelist.whitelistedIps,
    );

    return {
      clientId: Number(clientId),
      callerIdTag: String(state.callerIdTag || ""),
      enforceTLS: Boolean(state.enforceTLS),
      ipWhitelistRecords: refreshedRecords,
      persistedIpRecords: refreshedRecords,
    };
  },
);

export const deletePbxWhitelistedIpThunk = createAsyncThunk(
  "pbxConfig/deleteWhitelistedIp",
  async ({ clientId, ipAddress }, { getState, rejectWithValue }) => {
    const normalizedIp = String(ipAddress || "").trim();
    if (!normalizedIp) {
      return rejectWithValue("Invalid IP address.");
    }

    const state = getState().pbxConfig;
    const records = normalizeWhitelistedRecords(state.ipWhitelistRecords);
    const target = records.find((item) => item.ip_address === normalizedIp);

    // If record has not been persisted yet, remove it locally only.
    if (!target?.id) {
      return {
        ipAddress: normalizedIp,
        removePersistedRecord: false,
      };
    }

    const response = await deleteWhitelistedIpById(target.id);
    if (response?.error) {
      return rejectWithValue(
        response.error.message || "Unable to delete whitelisted IP.",
      );
    }

    return {
      ipAddress: normalizedIp,
      removePersistedRecord: true,
      clientId: Number(clientId || state.clientId || 0) || null,
    };
  },
);

export const parsePbxEmailThunk = createAsyncThunk(
  "pbxConfig/parseEmail",
  async ({ clientId, emailBody, delimiter }, { rejectWithValue }) => {
    const normalizedEmailBody = normalizeMultilineText(emailBody).trim();
    if (!normalizedEmailBody) {
      return rejectWithValue("Paste an email body to test parsing.");
    }

    const resolvedDelimiter = resolveParserDelimiter(delimiter);

    let response = await parseClientEmail({
      client_id: clientId,
      email_body: normalizedEmailBody,
      delimiter: resolvedDelimiter,
    });

    if (response?.error) {
      return rejectWithValue(
        response.error.message || "Unable to parse sample email.",
      );
    }

    let parserData = extractParserData(response);

    // Retry once with '-' when tag-like delimiter was provided but parser returned no fields.
    if (!hasParserValues(parserData) && resolvedDelimiter !== "-") {
      response = await parseClientEmail({
        client_id: clientId,
        email_body: normalizedEmailBody,
        delimiter: "-",
      });

      if (response?.error) {
        return rejectWithValue(
          response.error.message || "Unable to parse sample email.",
        );
      }

      parserData = extractParserData(response);
    }

    const responseEmailBody = normalizeMultilineText(
      response?.email_body || normalizedEmailBody,
    );

    if (!hasParserValues(parserData)) {
      const localParserData = parseEmailBodyLocally(responseEmailBody);
      if (hasParserValues(localParserData)) {
        parserData = localParserData;
      }
    }

    if (!hasParserValues(parserData)) {
      return rejectWithValue("Parser response did not include parser data.");
    }

    return {
      sourceEmailBody: parserData?.normalized || responseEmailBody,
      parsedPreview: formatParsedPreview(parserData),
    };
  },
);

const initialState = {
  clientId: null,
  callerIdTag: "",
  persistedCallerIdTag: "",
  enforceTLS: true,
  persistedEnforceTLS: true,
  ipWhitelistRecords: [],
  persistedIpRecords: [],
  newIpAddress: "",
  leftEmail: "",
  rightEmail: "",
  isParsing: false,
  parseError: "",
  isLoading: false,
  isSaving: false,
  hasLoaded: false,
  error: "",
};

export const pbxConfigInitialState = initialState;

const pbxConfigSlice = createSlice({
  name: "pbxConfig",
  initialState,
  reducers: {
    setCallerIdTag: (state, action) => {
      state.callerIdTag = String(action.payload || "");
    },
    setEnforceTLS: (state, action) => {
      state.enforceTLS = Boolean(action.payload);
    },
    setNewIpAddress: (state, action) => {
      state.newIpAddress = String(action.payload || "");
    },
    setLeftEmail: (state, action) => {
      state.leftEmail = String(action.payload || "");
    },
    setRightEmail: (state, action) => {
      state.rightEmail = String(action.payload || "");
    },
    setIpWhitelistRecords: (state, action) => {
      const nextIpList = sanitizeIpList(action.payload);
      const previousByIp = new Map(
        normalizeWhitelistedRecords(state.ipWhitelistRecords).map((item) => [
          item.ip_address,
          item,
        ]),
      );

      state.ipWhitelistRecords = nextIpList.map((ipAddress) => {
        const existing = previousByIp.get(ipAddress);
        if (existing) {
          return existing;
        }

        return {
          id: null,
          client_id: Number(state.clientId || 0) || null,
          ip_address: ipAddress,
          created_at: "",
          updated_at: "",
        };
      });
    },
    clearPbxConfigError: (state) => {
      state.error = "";
    },
    clearPbxParseError: (state) => {
      state.parseError = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadPbxConfigThunk.pending, (state) => {
        state.isLoading = true;
        state.error = "";
      })
      .addCase(loadPbxConfigThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.hasLoaded = true;
        state.error = action.payload.warning || "";
        state.clientId = Number(action.payload.clientId || 0) || null;
        state.callerIdTag = action.payload.callerIdTag;
        state.persistedCallerIdTag = action.payload.callerIdTag;
        state.enforceTLS = action.payload.enforceTLS;
        state.persistedEnforceTLS = action.payload.enforceTLS;
        state.ipWhitelistRecords = action.payload.ipWhitelistRecords;
        state.persistedIpRecords = action.payload.persistedIpRecords;
      })
      .addCase(loadPbxConfigThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.hasLoaded = true;
        state.error = action.payload || "Unable to load PBX configuration.";
      })
      .addCase(savePbxConfigThunk.pending, (state) => {
        state.isSaving = true;
        state.error = "";
      })
      .addCase(savePbxConfigThunk.fulfilled, (state, action) => {
        state.isSaving = false;
        state.error = "";
        state.clientId = Number(action.payload.clientId || 0) || null;
        state.callerIdTag = action.payload.callerIdTag;
        state.persistedCallerIdTag = action.payload.callerIdTag;
        state.enforceTLS = action.payload.enforceTLS;
        state.persistedEnforceTLS = action.payload.enforceTLS;
        state.ipWhitelistRecords = action.payload.ipWhitelistRecords;
        state.persistedIpRecords = action.payload.persistedIpRecords;
      })
      .addCase(savePbxConfigThunk.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload || "Unable to save PBX configuration.";
      })
      .addCase(deletePbxWhitelistedIpThunk.pending, (state) => {
        state.error = "";
      })
      .addCase(deletePbxWhitelistedIpThunk.fulfilled, (state, action) => {
        const targetIp = String(action.payload?.ipAddress || "").trim();
        const removePersistedRecord = Boolean(action.payload?.removePersistedRecord);

        state.ipWhitelistRecords = normalizeWhitelistedRecords(
          state.ipWhitelistRecords,
        ).filter((item) => item.ip_address !== targetIp);

        if (removePersistedRecord) {
          state.persistedIpRecords = normalizeWhitelistedRecords(
            state.persistedIpRecords,
          ).filter((item) => item.ip_address !== targetIp);
        }
      })
      .addCase(deletePbxWhitelistedIpThunk.rejected, (state, action) => {
        state.error = action.payload || "Unable to delete whitelisted IP.";
      })
      .addCase(parsePbxEmailThunk.pending, (state) => {
        state.isParsing = true;
        state.parseError = "";
      })
      .addCase(parsePbxEmailThunk.fulfilled, (state, action) => {
        state.isParsing = false;
        state.parseError = "";
        state.leftEmail = action.payload.sourceEmailBody;
        state.rightEmail = action.payload.parsedPreview;
      })
      .addCase(parsePbxEmailThunk.rejected, (state, action) => {
        state.isParsing = false;
        state.parseError = action.payload || "Unable to parse sample email.";
      });
  },
});

export const {
  clearPbxConfigError,
  clearPbxParseError,
  setCallerIdTag,
  setEnforceTLS,
  setIpWhitelistRecords,
  setLeftEmail,
  setNewIpAddress,
  setRightEmail,
} = pbxConfigSlice.actions;

export const selectPbxConfigState = (state) => state.pbxConfig;
export const selectPbxClientId = (state) => state.pbxConfig.clientId;
export const selectPbxCallerIdTag = (state) => state.pbxConfig.callerIdTag;
export const selectPbxEnforceTLS = (state) => state.pbxConfig.enforceTLS;
export const selectPbxWhitelistRecords = (state) => state.pbxConfig.ipWhitelistRecords;
export const selectPbxWhitelistIps = createSelector(
  [selectPbxWhitelistRecords],
  (records) => records.map((item) => item.ip_address),
);
export const selectPbxNewIpAddress = (state) => state.pbxConfig.newIpAddress;
export const selectPbxLeftEmail = (state) => state.pbxConfig.leftEmail;
export const selectPbxRightEmail = (state) => state.pbxConfig.rightEmail;
export const selectPbxLoading = (state) => state.pbxConfig.isLoading;
export const selectPbxSaving = (state) => state.pbxConfig.isSaving;
export const selectPbxIsParsing = (state) => state.pbxConfig.isParsing;
export const selectPbxParseError = (state) => state.pbxConfig.parseError;
export const selectPbxHasLoaded = (state) => state.pbxConfig.hasLoaded;
export const selectPbxError = (state) => state.pbxConfig.error;
export const selectPbxCanSaveChanges = createSelector(
  [
    selectPbxHasLoaded,
    selectPbxCallerIdTag,
    (state) => state.pbxConfig.persistedCallerIdTag,
    selectPbxEnforceTLS,
    (state) => state.pbxConfig.persistedEnforceTLS,
    selectPbxWhitelistRecords,
    (state) => state.pbxConfig.persistedIpRecords,
    selectPbxSaving,
  ],
  (
    hasLoaded,
    callerIdTag,
    persistedCallerIdTag,
    enforceTLS,
    persistedEnforceTLS,
    currentRecords,
    persistedRecords,
    isSaving,
  ) => {
    if (isSaving || !hasLoaded) {
      return false;
    }

    const normalizedCurrentTag = String(callerIdTag || "").trim();
    const normalizedPersistedTag = String(persistedCallerIdTag || "").trim();

    const normalizedCurrentIps = Array.from(
      new Set(
        (Array.isArray(currentRecords) ? currentRecords : [])
          .map((item) => String(item?.ip_address || "").trim())
          .filter(Boolean),
      ),
    ).sort();

    const normalizedPersistedIps = Array.from(
      new Set(
        (Array.isArray(persistedRecords) ? persistedRecords : [])
          .map((item) => String(item?.ip_address || "").trim())
          .filter(Boolean),
      ),
    ).sort();

    const isCallerIdChanged = normalizedCurrentTag !== normalizedPersistedTag;
    const isTlsChanged = Boolean(enforceTLS) !== Boolean(persistedEnforceTLS);
    const areIpsChanged =
      normalizedCurrentIps.length !== normalizedPersistedIps.length ||
      normalizedCurrentIps.some((ip, index) => ip !== normalizedPersistedIps[index]);

    return isCallerIdChanged || isTlsChanged || areIpsChanged;
  },
);

export default pbxConfigSlice.reducer;
